import { useState } from "react"
import { Task, type ConvertTaskToRecurringPayload, type UpdateTaskRecurrencePayload } from "@/schema"
import { Button } from "@ui/shad"
import { cn } from "@ui/utils"
import { format, formatDistanceToNow } from "date-fns"
import { Repeat, Trash2 } from "lucide-react"
import { toast } from "sonner"

import { useDeactivateTaskRecurrence, useUpdateTaskRecurrence } from "@/hooks/api/tasks"
import { ConfirmDialog } from "@/components/dialogs"
import { MakeRecurringDialog } from "@/components/tasks/make-recurring-dialog"
import { monthlyWeekdaySummary } from "@/components/tasks/utils/monthly-recurrence"

interface TaskRepeatBadgeProps {
  task: Task
  size?: "xs" | "sm"
  disabled?: boolean
}

export function TaskRepeatBadge({ task, size = "xs", disabled = false }: TaskRepeatBadgeProps) {
  const recurrence = task.task_recurrence
  const [showConfirm, setShowConfirm] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const deactivateMutation = useDeactivateTaskRecurrence()
  const updateMutation = useUpdateTaskRecurrence()

  const handleBadgeClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShowEditDialog(true)
  }

  const handleDialogToggle = (open: boolean) => {
    setShowConfirm(open)
  }

  if (!recurrence) return null

  const recurrenceDeactivated =
    !!recurrence.deactivated_at ||
    (deactivateMutation.isSuccess && deactivateMutation.variables?.recurrenceId === recurrence.id)
  const recurrenceActive = !recurrenceDeactivated
  const intervalValue = recurrence.interval_value ?? 1
  const intervalUnit = recurrence.interval_unit ?? "weeks"
  const UNIT_LABELS = {
    days: { singular: "day", plural: "days" },
    weeks: { singular: "week", plural: "weeks" },
    months: { singular: "month", plural: "months" },
    years: { singular: "year", plural: "years" },
  } as const
  const unitMeta = UNIT_LABELS[intervalUnit as keyof typeof UNIT_LABELS] ?? UNIT_LABELS.weeks
  const weekdaySummary = monthlyWeekdaySummary(
    recurrence.monthly_rule_type,
    recurrence.weekday_ordinal,
    recurrence.weekday
  )
  const intervalSummary =
    intervalValue === 1
      ? `Every ${unitMeta.singular}${weekdaySummary ? ` ${weekdaySummary}` : ""}`
      : `Every ${intervalValue} ${unitMeta.plural}${weekdaySummary ? ` ${weekdaySummary}` : ""}`
  const nextOccurrenceDate = recurrence.next_run_at ? new Date(recurrence.next_run_at) : null
  const nextOccurrenceRelative = nextOccurrenceDate
    ? formatDistanceToNow(nextOccurrenceDate, { addSuffix: true })
    : null
  const nextOccurrenceExact = nextOccurrenceDate ? format(nextOccurrenceDate, "MMM d, yyyy") : null
  const withinAWeek = Boolean(
    nextOccurrenceDate && Math.abs(nextOccurrenceDate.getTime() - Date.now()) <= 7 * 24 * 60 * 60 * 1000
  )
  const startDate = recurrence.start_at ? new Date(recurrence.start_at) : null
  const startExact = startDate ? format(startDate, "MMM d, yyyy") : null
  const startRelative = startDate ? formatDistanceToNow(startDate, { addSuffix: true }) : null
  const showStartRelative = Boolean(startDate && Math.abs(startDate.getTime() - Date.now()) <= 7 * 24 * 60 * 60 * 1000)

  const activeBadgeText = [
    intervalSummary,
    nextOccurrenceDate
      ? withinAWeek && nextOccurrenceRelative
        ? `next ${nextOccurrenceRelative}`
        : nextOccurrenceExact
          ? `next ${nextOccurrenceExact}`
          : null
      : startDate
        ? showStartRelative && startRelative
          ? `starting ${startRelative}`
          : startExact
            ? `starting ${startExact}`
            : null
        : null,
  ]
    .filter(Boolean)
    .join(" • ")

  const stoppedDate = recurrence.deactivated_at
    ? format(new Date(recurrence.deactivated_at), "MMM d, yyyy")
    : recurrenceDeactivated
      ? format(new Date(deactivateMutation.submittedAt), "MMM d, yyyy")
      : null
  const inactiveBadgeText = [intervalSummary, stoppedDate ? `stopped ${stoppedDate}` : "stopped"]
    .filter(Boolean)
    .join(" • ")

  const handleStop = () => {
    if (!recurrence.id) return

    deactivateMutation.mutate(
      { recurrenceId: recurrence.id },
      {
        onSuccess: () => {
          toast.success("Repeating stopped")
          setShowConfirm(false)
        },
      }
    )
  }

  const handleUpdate = async (data: ConvertTaskToRecurringPayload | UpdateTaskRecurrencePayload) => {
    if (!recurrence.id) return

    await updateMutation.mutateAsync({ recurrenceId: recurrence.id, data })
    toast.success("Repeating cadence updated")
    setShowEditDialog(false)
  }

  if (!recurrenceActive) {
    return (
      <div
        className={cn(
          "border-border text-muted-foreground inline-flex items-center gap-2 rounded-full border border-dashed px-2 py-1 text-xs",
          size === "sm" ? "text-sm" : "text-xs"
        )}
      >
        <Repeat className={size === "sm" ? "size-4" : "size-3"} />
        <span>{inactiveBadgeText}</span>
      </div>
    )
  }

  return (
    <>
      <div
        className={cn(
          "border-border text-muted-foreground pointer-events-auto inline-flex items-center overflow-hidden rounded-sm border border-dashed disabled:cursor-not-allowed disabled:opacity-50",
          size === "sm" ? "text-sm/none" : "text-xs/none",
          disabled && "opacity-75"
        )}
      >
        <Button
          type="button"
          variant="ghost"
          size={size}
          className={cn(
            "text-muted-foreground hover:bg-muted hover:text-foreground h-auto rounded-none border-0 px-2 py-1",
            size === "sm" ? "text-sm/none" : "text-xs/none"
          )}
          onClick={handleBadgeClick}
          disabled={updateMutation.isPending || disabled}
        >
          <Repeat className={size === "sm" ? "size-4" : "size-3"} />
          <span>{activeBadgeText || "Repeats"}</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size={size}
          className={cn(
            "text-muted-foreground hover:bg-destructive/10 hover:text-destructive h-auto rounded-none border-0 px-1.5 py-1",
            size === "sm" ? "text-sm/none" : "text-xs/none"
          )}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setShowConfirm(true)
          }}
          disabled={deactivateMutation.isPending || disabled}
        >
          <Trash2 className={size === "sm" ? "size-4" : "size-3"} />
        </Button>
      </div>

      {showEditDialog && (
        <div onClick={(e) => e.stopPropagation()}>
          <MakeRecurringDialog
            task={task}
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            onSubmit={handleUpdate}
            submitting={updateMutation.isPending}
            mode="edit"
          />
        </div>
      )}

      {showConfirm && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmDialog
            open={showConfirm}
            onOpenChange={handleDialogToggle}
            title={"Stop repeating?"}
            description={"This will stop new occurrences from being scheduled."}
            dismiss={"explicit"}
            cancel={{ onClick: () => setShowConfirm(false) }}
            action={{ label: "Stop repeating", onClick: handleStop }}
          />
        </div>
      )}
    </>
  )
}
